import { useMemo, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getDate, getDatePlusMinus } from '@einhaus/meteoric-helpers';
import type {
    VolumePriceAnalysisEventType,
    BarDuration,
    ChartMarker,
    VolumePriceAnalysis,
    BarOHLCVDetailed,
    BarOHLCVDetailedWithStats
} from '@einhaus/project314-types';
import AdminLayout from '@/components/Layout/AdminLayout';
import ChartContainer from '@/components/Chart/ChartContainer';
import Alert from '@/components/UI/Alert';
import Button from '@/components/UI/Button';
import Card from '@/components/UI/Card';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import FormInput from '@/components/UI/FormInput';
import FormSelect from '@/components/UI/FormSelect';
import { StatusBadge } from '@/components/UI';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import { getEntityLink } from '@/utils/entityHelpers';
import { buildVpaEventExplanation, type AuditEventSensitivity } from './vpaAuditExplanation';

type AuditPreset = {
    duration: BarDuration;
    endDateTime: string;
    focusDateTime: string;
    label: string;
    symbol: string;
    windowDays: number;
};

type AuditEventStateRow = {
    bar: BarOHLCVDetailedWithStats;
    dateTime: string;
    eventTypes: VolumePriceAnalysisEventType[];
    state: VolumePriceAnalysis;
    timestamp: number;
};

const AUDIT_DURATION_OPTIONS: BarDuration[] = ['1m', '5m', '15m', '30m', '60m', '120m', '180m', '240m', '1d'];
const AUDIT_EVENT_SENSITIVITY_OPTIONS: AuditEventSensitivity[] = ['conservative', 'standard', 'aggressive'];
const DEFAULT_SYMBOL = 'CMCSA';
const DEFAULT_DURATION: BarDuration = '30m';
const DEFAULT_END_DATE_TIME = '2026-04-03 12:31:04';
const DEFAULT_FOCUS_DATE_TIME = '2026-03-09 11:00:00';
const DEFAULT_WINDOW_DAYS = 180;
const DEFAULT_AFTER_HOURS_REMOVAL = true;

const AUDIT_PRESETS: AuditPreset[] = [
    {
        duration: '5m',
        endDateTime: '2026-04-03 14:42:20',
        focusDateTime: '2026-03-09 15:50:00',
        label: 'CMCSA 5m Shakeout',
        symbol: 'CMCSA',
        windowDays: 180
    },
    {
        duration: '5m',
        endDateTime: '2026-04-03 14:42:20',
        focusDateTime: '2026-03-27 13:05:00',
        label: 'TTD 5m Upthrust',
        symbol: 'TTD',
        windowDays: 180
    },
    {
        duration: '15m',
        endDateTime: '2026-04-03 14:42:20',
        focusDateTime: '2026-03-09 10:30:00',
        label: 'CMCSA 15m Shakeout',
        symbol: 'CMCSA',
        windowDays: 180
    },
    {
        duration: '15m',
        endDateTime: '2026-04-03 14:42:20',
        focusDateTime: '2026-03-05 09:30:00',
        label: 'TTD 15m Upthrust',
        symbol: 'TTD',
        windowDays: 180
    },
    {
        duration: '30m',
        endDateTime: '2026-04-03 12:31:04',
        focusDateTime: '2026-03-09 10:30:00',
        label: 'CMCSA 30m Shakeout',
        symbol: 'CMCSA',
        windowDays: 180
    },
    {
        duration: '30m',
        endDateTime: '2026-04-03 12:31:04',
        focusDateTime: '2026-03-05 09:30:00',
        label: 'TTD 30m Upthrust',
        symbol: 'TTD',
        windowDays: 180
    },
    {
        duration: '60m',
        endDateTime: '2026-04-03 14:51:37',
        focusDateTime: '2026-01-05 10:00:00',
        label: 'CMCSA 60m Shakeout',
        symbol: 'CMCSA',
        windowDays: 180
    },
    {
        duration: '60m',
        endDateTime: '2026-04-03 14:51:37',
        focusDateTime: '2026-03-05 09:00:00',
        label: 'TTD 60m Upthrust',
        symbol: 'TTD',
        windowDays: 180
    },
    {
        duration: '120m',
        endDateTime: '2026-04-03 14:51:37',
        focusDateTime: '2025-11-21 10:00:00',
        label: 'NVDA 120m Shakeout',
        symbol: 'NVDA',
        windowDays: 180
    },
    {
        duration: '120m',
        endDateTime: '2026-04-03 14:51:37',
        focusDateTime: '2026-03-05 08:00:00',
        label: 'TTD 120m Upthrust',
        symbol: 'TTD',
        windowDays: 180
    },
    {
        duration: '180m',
        endDateTime: '2026-04-03 14:51:37',
        focusDateTime: '2025-10-31 09:00:00',
        label: 'CMCSA 180m Shakeout',
        symbol: 'CMCSA',
        windowDays: 180
    },
    {
        duration: '180m',
        endDateTime: '2026-04-03 14:51:37',
        focusDateTime: '2026-03-05 09:00:00',
        label: 'TTD 180m Upthrust',
        symbol: 'TTD',
        windowDays: 180
    },
    {
        duration: '240m',
        endDateTime: '2026-04-03 14:51:59',
        focusDateTime: '2025-10-31 08:00:00',
        label: 'CMCSA 240m Shakeout',
        symbol: 'CMCSA',
        windowDays: 180
    },
    {
        duration: '240m',
        endDateTime: '2026-04-03 14:51:59',
        focusDateTime: '2026-03-05 08:00:00',
        label: 'TTD 240m Upthrust',
        symbol: 'TTD',
        windowDays: 180
    },
    {
        duration: '1m',
        endDateTime: getDate({ format: 'ymdhms' }),
        focusDateTime: '',
        label: 'SPY Intraday',
        symbol: 'SPY',
        windowDays: 10
    }
];

const isAuditDuration = (value: string | null): value is BarDuration => {
    if (!value) return false;
    return AUDIT_DURATION_OPTIONS.includes(value as BarDuration);
};

const isAuditEventSensitivity = (value: string | null): value is AuditEventSensitivity => {
    if (!value) return false;
    return AUDIT_EVENT_SENSITIVITY_OPTIONS.includes(value as AuditEventSensitivity);
};

const parseBooleanSearchParam = (value: string | null, defaultValue: boolean): boolean => {
    if (value === null) return defaultValue;
    if (value === '1' || value.toLowerCase() === 'true') return true;
    if (value === '0' || value.toLowerCase() === 'false') return false;
    return defaultValue;
};

const parsePositiveInteger = (value: string | null, fallbackValue: number): number => {
    const parsed = Number(value);

    if (!Number.isInteger(parsed) || parsed <= 0) {
        return fallbackValue;
    }

    return parsed;
};

const getFormStringValue = (formData: FormData, key: string): string => {
    const value = formData.get(key);
    return typeof value === 'string' ? value : '';
};

const formatMetric = (value: number | null | undefined, fractionDigits = 2): string => {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return 'n/a';
    }

    return value.toFixed(fractionDigits);
};

const formatEventTypeLabel = (value: string): string =>
    value.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (firstLetter) => firstLetter.toUpperCase());

const formatEnumLabel = (value: string): string =>
    value.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (firstLetter) => firstLetter.toUpperCase());

const buildMarkerText = (eventType: string, directionalBias: string): string => {
    if (directionalBias === 'bullish') {
        return `Long ${formatEventTypeLabel(eventType)}`;
    }

    if (directionalBias === 'bearish') {
        return `Short ${formatEventTypeLabel(eventType)}`;
    }

    return `VPA ${formatEventTypeLabel(eventType)}`;
};

const getFocusedChartWindow = (params: {
    duration: BarDuration;
    fallbackEndDateTime: string;
    focusDateTime: string | null;
    windowDays: number;
}): { endDateTime: string; startDateTime: string } => {
    const { duration, fallbackEndDateTime, focusDateTime, windowDays } = params;

    if (!focusDateTime) {
        return {
            endDateTime: fallbackEndDateTime,
            startDateTime: getDatePlusMinus('-', windowDays, 'day', 'ymdhms', 'start', fallbackEndDateTime)
        };
    }

    const focusedWindow =
        duration === '1m'
            ? { beforeDays: 3, afterDays: 1 }
            : duration === '5m'
              ? { beforeDays: 8, afterDays: 2 }
              : duration === '15m'
                ? { beforeDays: 14, afterDays: 4 }
                : duration === '30m'
                  ? { beforeDays: 25, afterDays: 8 }
                  : duration === '60m'
                    ? { beforeDays: 50, afterDays: 15 }
                    : duration === '120m'
                      ? { beforeDays: 70, afterDays: 20 }
                      : duration === '180m'
                        ? { beforeDays: 90, afterDays: 25 }
                        : duration === '240m'
                          ? { beforeDays: 120, afterDays: 30 }
                          : duration === '1d'
                            ? { beforeDays: 180, afterDays: 45 }
                            : { beforeDays: 25, afterDays: 8 };

    return {
        endDateTime: getDatePlusMinus('+', focusedWindow.afterDays, 'day', 'ymdhms', 'end', focusDateTime),
        startDateTime: getDatePlusMinus('-', focusedWindow.beforeDays, 'day', 'ymdhms', 'start', focusDateTime)
    };
};

const VolumePriceAnalysisAudit = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const resolvedSymbol = (searchParams.get('symbol') ?? DEFAULT_SYMBOL).trim().toUpperCase();
    const durationSearchParam = searchParams.get('duration');
    const resolvedDuration: BarDuration = isAuditDuration(durationSearchParam) ? durationSearchParam : DEFAULT_DURATION;
    const resolvedEndDateTime = searchParams.get('endDateTime') ?? DEFAULT_END_DATE_TIME;
    const resolvedFocusDateTime = searchParams.get('focusDateTime');
    const resolvedWindowDays = parsePositiveInteger(searchParams.get('windowDays'), DEFAULT_WINDOW_DAYS);
    const eventSensitivitySearchParam = searchParams.get('eventSensitivity');

    const resolvedEventSensitivity: AuditEventSensitivity = isAuditEventSensitivity(eventSensitivitySearchParam)
        ? eventSensitivitySearchParam
        : 'standard';

    const resolvedShouldRemoveAfterHours = parseBooleanSearchParam(searchParams.get('shouldRemoveAfterHours'), DEFAULT_AFTER_HOURS_REMOVAL);

    const auditQueryStartDateTime = useMemo(
        () => getDatePlusMinus('-', resolvedWindowDays, 'day', 'ymdhms', 'start', resolvedEndDateTime),
        [resolvedEndDateTime, resolvedWindowDays]
    );

    const chartWindow = useMemo(
        () =>
            getFocusedChartWindow({
                duration: resolvedDuration,
                fallbackEndDateTime: resolvedEndDateTime,
                focusDateTime: resolvedFocusDateTime,
                windowDays: resolvedWindowDays
            }),
        [resolvedDuration, resolvedEndDateTime, resolvedFocusDateTime, resolvedWindowDays]
    );

    const symbolSearchQuery = useAppQuery({
        ...trpc.search.symbolSearch.queryOptions({
            term: resolvedSymbol
        }),
        enabled: resolvedSymbol.length > 0
    });

    const resolvedAssetSearchResult =
        symbolSearchQuery.data?.results.find((result) => result.entityType === 'Asset' && result.symbol.toUpperCase() === resolvedSymbol) ??
        null;

    const entityQuery = useAppQuery({
        ...trpc.entity.details.queryOptions({
            entityId: resolvedAssetSearchResult?.entityId ?? 0,
            entityType: 'Asset'
        }),
        enabled: resolvedAssetSearchResult !== null
    });

    const entity = entityQuery.data?.entity ?? null;

    const auditQuery = useAppQuery({
        ...trpc.entity.fetchVolumePriceAnalysisAudit.queryOptions({
            entityId: entity?.id ?? 0,
            entityType: 'Asset',
            startDateTime: auditQueryStartDateTime,
            endDateTime: resolvedEndDateTime,
            duration: resolvedDuration,
            eventSensitivity: resolvedEventSensitivity,
            shouldRemoveAfterHours: resolvedShouldRemoveAfterHours
        }),
        enabled: entity !== null
    });

    const fallbackBarsQuery = useAppQuery({
        ...trpc.entity.fetchBarsForDuration.queryOptions({
            entityId: entity?.id ?? 0,
            entityType: 'Asset',
            startDateTime: chartWindow.startDateTime,
            endDateTime: chartWindow.endDateTime,
            duration: resolvedDuration,
            shouldRemoveAfterHours: resolvedShouldRemoveAfterHours,
            shouldIncludeStats: false
        }),
        enabled: entity !== null && auditQuery.data?.eventStates.some((eventState) => !eventState.bar) === true
    });

    const sortedEvents = useMemo(
        () => [...(auditQuery.data?.events ?? [])].sort((a, b) => b.event.timestamp - a.event.timestamp),
        [auditQuery.data?.events]
    );

    const eventStatesByDateTime = useMemo(() => {
        const stateMap = new Map<string, AuditEventStateRow>();

        for (const eventState of auditQuery.data?.eventStates ?? []) {
            stateMap.set(eventState.dateTime, eventState);
        }

        return stateMap;
    }, [auditQuery.data?.eventStates]);

    const selectedDateTime = resolvedFocusDateTime ?? sortedEvents[0]?.dateTime ?? null;

    const selectedEvents = useMemo(
        () => (selectedDateTime ? sortedEvents.filter((eventRow) => eventRow.dateTime === selectedDateTime) : []),
        [selectedDateTime, sortedEvents]
    );

    const selectedEventState = selectedDateTime ? (eventStatesByDateTime.get(selectedDateTime) ?? null) : null;
    const selectedEventTimestamp = selectedEventState?.timestamp ?? selectedEvents[0]?.event.timestamp ?? null;

    const fallbackSelectedEventBar =
        selectedEventTimestamp !== null ? (fallbackBarsQuery.data?.bars.find((bar) => bar.t === selectedEventTimestamp) ?? null) : null;

    const selectedEventBar: BarOHLCVDetailed | BarOHLCVDetailedWithStats | null =
        selectedEventState?.bar ?? fallbackSelectedEventBar ?? null;

    const selectedEventExplanations = useMemo(
        () =>
            selectedEventState && selectedEventBar
                ? selectedEvents.map((eventRow) => ({
                      event: eventRow.event,
                      explanation: buildVpaEventExplanation({
                          bar: selectedEventBar,
                          event: eventRow.event,
                          eventSensitivity: resolvedEventSensitivity,
                          state: selectedEventState.state
                      })
                  }))
                : [],
        [resolvedEventSensitivity, selectedEventBar, selectedEventState, selectedEvents]
    );

    const eventCounts = useMemo(() => {
        const counts = new Map<string, number>();

        for (const eventRow of sortedEvents) {
            counts.set(eventRow.event.type, (counts.get(eventRow.event.type) ?? 0) + 1);
        }

        return [...counts.entries()]
            .map(([type, count]) => ({ count, type }))
            .sort((a, b) => b.count - a.count || a.type.localeCompare(b.type));
    }, [sortedEvents]);

    const markers = useMemo<ChartMarker[]>(
        () =>
            sortedEvents.map((eventRow) => ({
                dateTime: eventRow.dateTime,
                position:
                    eventRow.event.directionalBias === 'bullish'
                        ? 'belowBar'
                        : eventRow.event.directionalBias === 'bearish'
                          ? 'aboveBar'
                          : 'inBar',
                text: buildMarkerText(eventRow.event.type, eventRow.event.directionalBias)
            })),
        [sortedEvents]
    );

    const handleApplyFilters = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const rawSymbol = getFormStringValue(formData, 'symbol');
        const rawDuration = getFormStringValue(formData, 'duration');
        const rawEndDateTime = getFormStringValue(formData, 'endDateTime');
        const rawFocusDateTime = getFormStringValue(formData, 'focusDateTime');
        const rawWindowDays = getFormStringValue(formData, 'windowDays');
        const rawEventSensitivity = getFormStringValue(formData, 'eventSensitivity');
        const nextDuration = isAuditDuration(rawDuration) ? rawDuration : DEFAULT_DURATION;
        const nextEventSensitivity = isAuditEventSensitivity(rawEventSensitivity) ? rawEventSensitivity : 'standard';
        const normalizedFocusDateTime = rawFocusDateTime.trim();

        const nextSearchParams = new URLSearchParams();
        nextSearchParams.set('symbol', rawSymbol.trim().toUpperCase() || DEFAULT_SYMBOL);
        nextSearchParams.set('duration', nextDuration);
        nextSearchParams.set('endDateTime', rawEndDateTime.trim() || DEFAULT_END_DATE_TIME);
        nextSearchParams.set('windowDays', String(parsePositiveInteger(rawWindowDays, DEFAULT_WINDOW_DAYS)));
        nextSearchParams.set('eventSensitivity', nextEventSensitivity);
        nextSearchParams.set('shouldRemoveAfterHours', formData.get('shouldRemoveAfterHours') ? '1' : '0');

        if (normalizedFocusDateTime.length > 0) {
            nextSearchParams.set('focusDateTime', normalizedFocusDateTime);
        }

        setSearchParams(nextSearchParams);
    };

    const handlePresetSelect = (preset: AuditPreset) => {
        const nextSearchParams = new URLSearchParams();
        nextSearchParams.set('symbol', preset.symbol);
        nextSearchParams.set('duration', preset.duration);
        nextSearchParams.set('endDateTime', preset.endDateTime);
        nextSearchParams.set('windowDays', String(preset.windowDays));
        nextSearchParams.set('eventSensitivity', 'standard');
        nextSearchParams.set('shouldRemoveAfterHours', '1');

        if (preset.focusDateTime.length > 0) {
            nextSearchParams.set('focusDateTime', preset.focusDateTime);
        }

        setSearchParams(nextSearchParams);
    };

    const handleFocusEvent = (dateTime: string) => {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set('focusDateTime', dateTime);
        setSearchParams(nextSearchParams);
    };

    const handleClearFocus = () => {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete('focusDateTime');
        setSearchParams(nextSearchParams);
    };

    const activeError =
        symbolSearchQuery.error?.message ??
        entityQuery.error?.message ??
        auditQuery.error?.message ??
        fallbackBarsQuery.error?.message ??
        (!resolvedAssetSearchResult && resolvedSymbol.length > 0 && !symbolSearchQuery.isLoading
            ? `No Asset found for symbol ${resolvedSymbol}`
            : null);

    return (
        <AdminLayout containerSize="2200px" title="Volume Price Analysis Audit">
            <div className="space-y-6">
                <Card
                    bodyClassName="space-y-5"
                    subtitle="Manual review surface for VPA events, context, and chart inspection."
                    title="VPA Audit"
                >
                    <form className="space-y-4" key={searchParams.toString()} onSubmit={handleApplyFilters}>
                        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
                            <FormInput
                                noMargin
                                defaultValue={resolvedSymbol}
                                helpText="Exact asset symbol"
                                id="vpa-audit-symbol"
                                label="Symbol"
                                name="symbol"
                            />
                            <FormSelect
                                noMargin
                                defaultValue={resolvedDuration}
                                id="vpa-audit-duration"
                                label="Duration"
                                name="duration"
                                options={AUDIT_DURATION_OPTIONS.map((duration) => ({
                                    label: duration,
                                    value: duration
                                }))}
                            />
                            <FormInput
                                noMargin
                                defaultValue={resolvedEndDateTime}
                                helpText="YYYY-MM-DD or YYYY-MM-DD HH:MM:SS"
                                id="vpa-audit-end"
                                label="End Date/Time"
                                name="endDateTime"
                            />
                            <FormInput
                                noMargin
                                defaultValue={resolvedFocusDateTime ?? ''}
                                helpText="Optional focused bar. Leave blank to review the full window."
                                id="vpa-audit-focus"
                                label="Focus Date/Time"
                                name="focusDateTime"
                                placeholder={DEFAULT_FOCUS_DATE_TIME}
                            />
                            <FormInput
                                noMargin
                                defaultValue={String(resolvedWindowDays)}
                                helpText="History loaded into the audit table"
                                id="vpa-audit-window-days"
                                label="Window Days"
                                name="windowDays"
                            />
                            <FormSelect
                                noMargin
                                defaultValue={resolvedEventSensitivity}
                                id="vpa-audit-sensitivity"
                                label="Event Sensitivity"
                                name="eventSensitivity"
                                options={AUDIT_EVENT_SENSITIVITY_OPTIONS.map((sensitivity) => ({
                                    label: formatEnumLabel(sensitivity),
                                    value: sensitivity
                                }))}
                            />
                        </div>

                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <label className="flex items-center gap-2 text-sm text-zinc-300">
                                <input
                                    className="h-4 w-4 rounded border-zinc-700 bg-zinc-900"
                                    defaultChecked={resolvedShouldRemoveAfterHours}
                                    name="shouldRemoveAfterHours"
                                    type="checkbox"
                                />
                                Remove after-hours bars
                            </label>

                            <div className="flex flex-wrap items-center gap-2">
                                <Button type="submit" variant="primary">
                                    Apply
                                </Button>
                                <Button type="button" variant="secondary" onClick={handleClearFocus}>
                                    Clear Focus
                                </Button>
                            </div>
                        </div>
                    </form>

                    <div className="flex flex-wrap gap-2">
                        {AUDIT_PRESETS.map((preset) => (
                            <Button
                                key={preset.label}
                                size="sm"
                                type="button"
                                variant="outline-dark"
                                onClick={() => handlePresetSelect(preset)}
                            >
                                {preset.label}
                            </Button>
                        ))}
                    </div>

                    {activeError && <Alert variant="danger">{activeError}</Alert>}
                </Card>

                <div className="grid gap-6 xl:grid-cols-[minmax(0,1.6fr)_minmax(360px,0.9fr)]">
                    <Card
                        bodyClassName="space-y-4"
                        subtitle={
                            entity
                                ? `${entity.symbol} · ${resolvedDuration} · query ${auditQueryStartDateTime} -> ${resolvedEndDateTime} · chart ${chartWindow.startDateTime} -> ${chartWindow.endDateTime}`
                                : 'Resolve an asset to load the audit chart.'
                        }
                        title="Chart Review"
                    >
                        {entity && (
                            <div className="flex flex-wrap items-center gap-3 text-sm text-zinc-400">
                                <Link
                                    className="text-blue-400 hover:text-blue-300 underline"
                                    to={`${getEntityLink(entity)}?duration=${resolvedDuration}`}
                                >
                                    Open Full Entity Page
                                </Link>
                                <span>Markers: {markers.length}</span>
                                <span>Event bars: {auditQuery.data?.eventStates.length ?? 0}</span>
                                <span>Focus: {selectedDateTime ?? 'none'}</span>
                            </div>
                        )}

                        {entity && auditQuery.isLoading ? (
                            <div className="min-h-[720px] flex items-center justify-center">
                                <DataLoadingSpinner text="Loading VPA audit data..." />
                            </div>
                        ) : entity ? (
                            <div className="min-h-[760px]">
                                <ChartContainer
                                    hideControls
                                    duration={resolvedDuration}
                                    endDateTime={chartWindow.endDateTime}
                                    entity={entity}
                                    markersOverride={markers}
                                    shouldIncludeChartPatterns={false}
                                    shouldShowChartPatternPanel={false}
                                    startDateTime={chartWindow.startDateTime}
                                />
                            </div>
                        ) : (
                            <div className="min-h-[320px] flex items-center justify-center text-zinc-500">
                                Resolve an asset symbol to render the audit chart.
                            </div>
                        )}
                    </Card>

                    <div className="space-y-6">
                        <Card
                            subtitle={
                                auditQuery.data?.latestState
                                    ? auditQuery.data.latestState.dateTime
                                    : 'No VPA state available in the current window.'
                            }
                            title="Latest VPA State"
                        >
                            {auditQuery.isLoading ? (
                                <DataLoadingSpinner text="Loading latest state..." />
                            ) : auditQuery.data?.latestState ? (
                                <div className="grid gap-3 sm:grid-cols-2">
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Summary</div>
                                        <div className="mt-2 space-y-1 text-sm text-zinc-300">
                                            <div>Dominant: {auditQuery.data.latestState.state.summary.dominantSignal ?? 'n/a'}</div>
                                            <div>Bias: {auditQuery.data.latestState.state.summary.directionalBias}</div>
                                            <div>Confidence: {formatMetric(auditQuery.data.latestState.state.summary.confidenceScore)}</div>
                                            <div>Effort/Result: {auditQuery.data.latestState.state.summary.effortResultState}</div>
                                            <div>Volume State: {auditQuery.data.latestState.state.summary.volumeState}</div>
                                            <div>Spread State: {auditQuery.data.latestState.state.summary.spreadState}</div>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Context</div>
                                        <div className="mt-2 space-y-1 text-sm text-zinc-300">
                                            <div>
                                                RVOL TOD: {formatMetric(auditQuery.data.latestState.state.participation.relativeVolumeTod)}
                                            </div>
                                            <div>
                                                Trade Count TOD:{' '}
                                                {formatMetric(auditQuery.data.latestState.state.participation.relativeTradeCountTod)}
                                            </div>
                                            <div>
                                                Notional TOD:{' '}
                                                {formatMetric(auditQuery.data.latestState.state.participation.relativeNotionalTod)}
                                            </div>
                                            <div>Congestion Bars: {auditQuery.data.latestState.state.context.congestionBars}</div>
                                            <div>
                                                Breakout Quality:{' '}
                                                {formatMetric(auditQuery.data.latestState.state.context.breakoutFromCongestionQuality)}
                                            </div>
                                            <div>
                                                Trade-backed:{' '}
                                                {auditQuery.data.latestState.state.context.hasTradeBackedParticipation ? 'yes' : 'no'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-sm text-zinc-500">No latest VPA state returned for this window.</div>
                            )}
                        </Card>

                        <Card subtitle={selectedDateTime ?? 'Select a VPA event to inspect a focused bar state.'} title="Focused Signal">
                            {selectedEventState ? (
                                <div className="space-y-4">
                                    <div className="flex flex-wrap gap-2">
                                        {selectedEventState.eventTypes.map((eventType) => (
                                            <span
                                                className="rounded-full border border-zinc-700 bg-zinc-900 px-2 py-1 text-xs text-zinc-300"
                                                key={`${selectedEventState.dateTime}-${eventType}`}
                                            >
                                                {formatEventTypeLabel(eventType)}
                                            </span>
                                        ))}
                                    </div>

                                    <div className="grid gap-3 sm:grid-cols-2 text-sm text-zinc-300">
                                        <div>Bias: {selectedEventState.state.summary.directionalBias}</div>
                                        <div>Confidence: {formatMetric(selectedEventState.state.summary.confidenceScore)}</div>
                                        <div>Volume State: {selectedEventState.state.summary.volumeState}</div>
                                        <div>Spread State: {selectedEventState.state.summary.spreadState}</div>
                                        <div>Effort/Result: {selectedEventState.state.summary.effortResultState}</div>
                                        <div>Anomaly: {selectedEventState.state.summary.isEffortResultAnomaly ? 'yes' : 'no'}</div>
                                        <div>RVOL: {formatMetric(selectedEventState.state.participation.relativeVolume)}</div>
                                        <div>RVOL TOD: {formatMetric(selectedEventState.state.participation.relativeVolumeTod)}</div>
                                        <div>CLV: {formatMetric(selectedEventState.state.barStructure.closeLocationValue)}</div>
                                        <div>Body % Range: {formatMetric(selectedEventState.state.barStructure.bodyPctOfRange)}</div>
                                        <div>Upper Wick %: {formatMetric(selectedEventState.state.barStructure.upperWickPct)}</div>
                                        <div>Lower Wick %: {formatMetric(selectedEventState.state.barStructure.lowerWickPct)}</div>
                                        <div>
                                            Breakout Quality: {formatMetric(selectedEventState.state.context.breakoutFromCongestionQuality)}
                                        </div>
                                    </div>

                                    {selectedEventExplanations.length > 0 && (
                                        <div className="space-y-2">
                                            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Why This Fired</div>
                                            {selectedEventExplanations.map(({ event, explanation }) => (
                                                <div
                                                    className="rounded border border-zinc-800 bg-zinc-950/60 p-3 text-sm text-zinc-300"
                                                    key={event.type}
                                                >
                                                    <div className="font-medium text-white">{formatEventTypeLabel(event.type)}</div>
                                                    <div className="mt-1 text-sm text-zinc-400">{explanation.summary}</div>
                                                    <div className="mt-3 grid gap-3 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
                                                        <div className="space-y-2">
                                                            {explanation.predicates.map((predicate) => (
                                                                <div
                                                                    className="rounded border border-zinc-800 bg-zinc-900/70 p-3"
                                                                    key={`${event.type}-${predicate.label}`}
                                                                >
                                                                    <div className="flex items-center justify-between gap-3">
                                                                        <div className="font-medium text-white">{predicate.label}</div>
                                                                        <StatusBadge status={predicate.passed ? 'passed' : 'failed'}>
                                                                            {predicate.passed ? 'pass' : 'fail'}
                                                                        </StatusBadge>
                                                                    </div>
                                                                    <div className="mt-1 text-zinc-300">
                                                                        Actual: {predicate.actualValue}
                                                                    </div>
                                                                    <div className="text-zinc-400">Expected: {predicate.expectedValue}</div>
                                                                    {predicate.note && (
                                                                        <div className="mt-1 text-xs text-zinc-500">{predicate.note}</div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>

                                                        <div>
                                                            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">
                                                                Bar Snapshot
                                                            </div>
                                                            <div className="mt-2 grid gap-2 sm:grid-cols-2">
                                                                {explanation.rawBarMetrics.map((metric) => (
                                                                    <div
                                                                        className="rounded border border-zinc-800 bg-zinc-900/70 px-3 py-2"
                                                                        key={`${event.type}-${metric.label}`}
                                                                    >
                                                                        <div className="text-xs uppercase tracking-[0.12em] text-zinc-500">
                                                                            {metric.label}
                                                                        </div>
                                                                        <div className="mt-1 text-sm text-zinc-200">{metric.value}</div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <div className="mt-2 text-xs text-zinc-500">
                                                                Event confidence: {formatMetric(event.confidenceScore)}. Data flags:{' '}
                                                                {event.dataQualityFlags ?? 'n/a'}.
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {selectedEvents.length > 0 && !selectedEventBar && (
                                        <Alert variant="warning">
                                            The audit API did not return the raw focused bar payload. Restart the gateway on the updated
                                            backend build to enable exact predicate explanations.
                                        </Alert>
                                    )}
                                </div>
                            ) : (
                                <div className="text-sm text-zinc-500">No focused VPA event is selected in the current window.</div>
                            )}
                        </Card>

                        <Card subtitle={`${eventCounts.length} unique event types in the loaded window.`} title="Event Mix">
                            {eventCounts.length > 0 ? (
                                <div className="space-y-2">
                                    {eventCounts.map((eventCount) => (
                                        <div
                                            className="flex items-center justify-between rounded border border-zinc-800 bg-zinc-950/60 px-3 py-2 text-sm"
                                            key={eventCount.type}
                                        >
                                            <span className="text-zinc-300">{formatEventTypeLabel(eventCount.type)}</span>
                                            <span className="font-medium text-white">{eventCount.count}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-sm text-zinc-500">No VPA events were emitted for this audit window.</div>
                            )}
                        </Card>
                    </div>
                </div>

                <Card subtitle="Click a row to refocus the chart around that VPA event." title={`Event Timeline (${sortedEvents.length})`}>
                    {auditQuery.isLoading ? (
                        <DataLoadingSpinner text="Loading VPA events..." />
                    ) : sortedEvents.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-zinc-800 text-sm">
                                <thead className="bg-zinc-950/60 text-left text-zinc-400">
                                    <tr>
                                        <th className="px-3 py-2 font-medium">Date/Time</th>
                                        <th className="px-3 py-2 font-medium">Type</th>
                                        <th className="px-3 py-2 font-medium">Bias</th>
                                        <th className="px-3 py-2 font-medium">Confidence</th>
                                        <th className="px-3 py-2 font-medium">RVOL TOD</th>
                                        <th className="px-3 py-2 font-medium">CLV</th>
                                        <th className="px-3 py-2 font-medium">Breakout Q</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-zinc-900">
                                    {sortedEvents.map((eventRow) => {
                                        const isSelected = selectedDateTime === eventRow.dateTime;

                                        return (
                                            <tr
                                                className={`cursor-pointer ${isSelected ? 'bg-blue-500/10' : 'hover:bg-zinc-900/60'}`}
                                                key={`${eventRow.dateTime}-${eventRow.event.type}`}
                                                onClick={() => handleFocusEvent(eventRow.dateTime)}
                                            >
                                                <td className="px-3 py-2 text-zinc-200">{eventRow.dateTime}</td>
                                                <td className="px-3 py-2 text-white">{formatEventTypeLabel(eventRow.event.type)}</td>
                                                <td className="px-3 py-2 text-zinc-300">
                                                    {formatEnumLabel(eventRow.event.directionalBias)}
                                                </td>
                                                <td className="px-3 py-2 text-zinc-300">{formatMetric(eventRow.event.confidenceScore)}</td>
                                                <td className="px-3 py-2 text-zinc-300">
                                                    {formatMetric(eventRow.event.relativeVolumeTod)}
                                                </td>
                                                <td className="px-3 py-2 text-zinc-300">
                                                    {formatMetric(eventRow.event.closeLocationValue)}
                                                </td>
                                                <td className="px-3 py-2 text-zinc-300">
                                                    {formatMetric(eventRow.event.breakoutFromCongestionQuality)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-sm text-zinc-500">No VPA events were returned for the selected symbol and window.</div>
                    )}
                </Card>
            </div>
        </AdminLayout>
    );
};

export default VolumePriceAnalysisAudit;
